// Keep currency menus within the visible viewport (and the physical-card
// modal's scroll area). Native menus stay inside their dialog/focus trap.
function visibleBounds(menu, portaled, margin = 8) {
  const viewport = window.visualViewport;
  const bounds = {
    top: (viewport?.offsetTop || 0) + margin,
    left: (viewport?.offsetLeft || 0) + margin,
    bottom: (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight) - margin,
    right: (viewport?.offsetLeft || 0) + (viewport?.width || document.documentElement.clientWidth) - margin,
  };
  if (!portaled) {
    for (let parent = menu.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
      const style = getComputedStyle(parent);
      const rect = parent.getBoundingClientRect();
      if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
        bounds.top = Math.max(bounds.top, rect.top + parent.clientTop);
        bounds.bottom = Math.min(bounds.bottom, rect.top + parent.clientTop + parent.clientHeight);
      }
      if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
        bounds.left = Math.max(bounds.left, rect.left + parent.clientLeft);
        bounds.right = Math.min(bounds.right, rect.left + parent.clientLeft + parent.clientWidth);
      }
    }
  }
  return bounds;
}

function minimumMenuHeight(menu) {
  const style = getComputedStyle(menu);
  const rows = [...menu.querySelectorAll(':scope > .vs__dropdown-option, :scope > .currency-dropdown__option')].slice(0, 2);
  return rows.reduce((height, row) => {
    const rowStyle = getComputedStyle(row);
    return height + row.getBoundingClientRect().height + parseFloat(rowStyle.marginTop) + parseFloat(rowStyle.marginBottom);
  }, parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth));
}

function followMenu(menu, trigger, portaled = false, closeMenu) {
  let frame;
  let closed = false;
  let originalSlot;
  const anchorElement = portaled ? trigger : menu.offsetParent || trigger;
  const compact = !!menu.querySelector('.money-currency-option--code');
  const rightAligned = compact || menu.classList.contains('money-currency-menu--right');

  const position = () => {
    if (closed || !menu.isConnected || !menu.getClientRects().length) return;
    let bounds = visibleBounds(menu, portaled);
    const minimumHeight = minimumMenuHeight(menu);
    const anchor = anchorElement.getBoundingClientRect();
    // A scrolling body can leave too little room on either side of the field.
    // Move the open menu into its dialog panel before the actions, preserving
    // focus and Tab order, and restore its DOM location on close.
    const panel = !portaled && menu.closest('.user-modal__panel');
    const bodySpace = Math.max(0, anchor.top - bounds.top, bounds.bottom - anchor.bottom);
    if (!originalSlot && panel && menu.parentElement !== panel && bodySpace < minimumHeight) {
      originalSlot = document.createComment('currency-menu');
      menu.before(originalSlot);
      const focused = menu.contains(document.activeElement) ? document.activeElement : null;
      panel.insertBefore(menu, panel.querySelector(':scope > .user-modal__actions'));
      focused?.focus({ preventScroll: true });
      bounds = visibleBounds(menu, portaled);
    }
    const parent = portaled ? null : menu.offsetParent;
    const triggerBounds = trigger.getBoundingClientRect();
    const triggerClip = visibleBounds(trigger, false, 0);
    if (triggerBounds.bottom <= triggerClip.top || triggerBounds.top >= triggerClip.bottom ||
        triggerBounds.right <= triggerClip.left || triggerBounds.left >= triggerClip.right) {
      closed = true;
      closeMenu();
      return;
    }
    const flag = trigger.querySelector('img')?.getBoundingClientRect();
    const width = Math.min(compact ? 100 : 172, Math.max(0, bounds.right - bounds.left));
    menu.style.width = `${width}px`;
    menu.style.minWidth = `${width}px`;
    menu.style.margin = '0';
    menu.style.transform = 'none';
    menu.style.right = 'auto';
    menu.style.bottom = 'auto';
    const naturalHeight = Math.min(350, menu.scrollHeight + 2);
    const below = Math.max(0, bounds.bottom - anchor.bottom);
    const above = Math.max(0, anchor.top - bounds.top);
    const upwards = below < naturalHeight && above > below;
    // If neither side has enough room, overlap the field rather than leave a
    // strip of padding. The menu still fits within its visible container.
    const usableHeight = Math.min(minimumHeight, Math.max(0, bounds.bottom - bounds.top));
    const height = Math.min(naturalHeight, Math.max(upwards ? above : below, usableHeight));
    menu.style.maxHeight = `${height}px`;
    menu.dataset.currencyPlacement = upwards ? 'top' : 'bottom';
    const desiredLeft = rightAligned ? triggerBounds.right - width : (flag?.left ?? triggerBounds.left);
    const left = Math.max(bounds.left, Math.min(desiredLeft, bounds.right - width));
    const top = Math.max(bounds.top, Math.min(upwards ? anchor.top - height : anchor.bottom, bounds.bottom - height));
    const parentBounds = parent?.getBoundingClientRect();
    menu.style.left = `${left + (parent ? parent.scrollLeft - parentBounds.left - parent.clientLeft : window.scrollX)}px`;
    menu.style.top = `${top + (parent ? parent.scrollTop - parentBounds.top - parent.clientTop : window.scrollY)}px`;
    menu.style.visibility = '';
  };
  const schedule = event => {
    // Scrolling options must not move the menu or scroll the surrounding page.
    if (event?.type === 'scroll' && event.target instanceof Node && menu.contains(event.target)) return;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(position);
  };
  window.addEventListener('resize', schedule);
  window.addEventListener('scroll', schedule, true);
  window.visualViewport?.addEventListener('resize', schedule);
  window.visualViewport?.addEventListener('scroll', schedule);
  const observer = new ResizeObserver(schedule);
  observer.observe(menu);
  observer.observe(trigger);
  if (portaled) {
    // vue-select invokes calculatePosition before appending the menu to body.
    // Measure after that append, in the same task and before the first paint.
    queueMicrotask(position);
  } else {
    position();
  }
  return () => {
    closed = true;
    cancelAnimationFrame(frame);
    observer.disconnect();
    window.removeEventListener('resize', schedule);
    window.removeEventListener('scroll', schedule, true);
    window.visualViewport?.removeEventListener('resize', schedule);
    window.visualViewport?.removeEventListener('scroll', schedule);
    originalSlot?.replaceWith(menu);
  };
}

export function positionMoneyCurrencyMenu(menu, component) {
  return followMenu(menu, component.$refs.toggle, true, () => {
    component.open = false;
    component.searchEl.blur();
  });
}

let initialized = false;
export default function readyMoneyCurrencyMenus() {
  if (initialized) return;
  initialized = true;
  document.querySelectorAll('.money-currency-menu').forEach(menu => {
    let display;
    let stop;
    const sync = () => {
      if (display === menu.style.display) return;
      display = menu.style.display;
      stop?.();
      stop = null;
      if (display === 'none') return;
      const trigger = document.querySelector(`[aria-controls="${menu.id}"]`) || menu.closest('.currency-dropdown');
      if (trigger) stop = followMenu(menu, trigger, false, () => {
        if (menu.contains(document.activeElement)) trigger.focus({ preventScroll: true });
        menu.style.display = 'none';
        if (trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
        menu.querySelectorAll('[role="option"]').forEach(option => { option.tabIndex = -1; });
      });
    };
    new MutationObserver(sync).observe(menu, { attributes: true, attributeFilter: ['style'] });
    menu.addEventListener('arcadi:currency-menu-open', sync);
    sync();
  });
}
